import { useEffect, useMemo, useState } from 'react';

const WIZARD_STEPS = [
  { id: 1, label: 'Layout' },
  { id: 2, label: 'Colors' },
];

const LAYOUTS = [
  {
    id: 'sidebar-topbar',
    label: 'Sidebar + Topbar',
    description: 'Left navigation sidebar with a top bar for project & user info. Classic app shell — matches this tool’s own layout. On mobile, the sidebar tucks behind a menu button.',
  },
  {
    id: 'topbar-only',
    label: 'Topbar Only',
    description: 'Full-width top navigation bar, no sidebar. Maximizes horizontal space for content-heavy screens. On mobile, navigation moves to a bottom tab bar.',
  },
  {
    id: 'compact-sidebar',
    label: 'Compact Sidebar',
    description: 'Icon-only collapsed sidebar by default. Keeps navigation close at hand while freeing up room for content. On mobile, it becomes a floating menu button.',
  },
];

// Six independently-customizable color roles, grouped for the custom-colors
// form. Every preset below supplies a default for each; the wizard's text
// fields / pickers can override any of them individually.
const COLOR_FIELDS = [
  { key: 'menuBg',          group: 'Menu',    label: 'Menu Background Color' },
  { key: 'menuText',        group: 'Menu',    label: 'Menu Text Color' },
  { key: 'topbarBg',        group: 'Topbar',  label: 'Topbar Background Color' },
  { key: 'topbarText',      group: 'Topbar',  label: 'Topbar Text Color' },
  { key: 'buttonPrimary',   group: 'Buttons', label: 'Primary Button Color' },
  { key: 'buttonSecondary', group: 'Buttons', label: 'Secondary Button Color' },
  { key: 'buttonText',      group: 'Buttons', label: 'Button Text Color' },
  { key: 'listingAccent',   group: 'Content', label: 'Listing Color' },
  { key: 'pageBg',          group: 'Content', label: 'Page Background Color' },
];

const COLOR_SCHEMES = [
  {
    id: 'orange', label: 'Enable Orange',
    menuBg: '#E65100', menuText: '#FFFFFF',
    topbarBg: '#FFFFFF', topbarText: '#37474F',
    buttonPrimary: '#FF8A65', buttonSecondary: '#37474F', buttonText: '#FFFFFF',
    listingAccent: '#E65100', pageBg: '#FFF8F3',
  },
  {
    id: 'blue', label: 'Ocean Blue',
    menuBg: '#1565C0', menuText: '#FFFFFF',
    topbarBg: '#FFFFFF', topbarText: '#37474F',
    buttonPrimary: '#42A5F5', buttonSecondary: '#37474F', buttonText: '#FFFFFF',
    listingAccent: '#1565C0', pageBg: '#F2F8FF',
  },
  {
    id: 'green', label: 'Forest Green',
    menuBg: '#2E7D32', menuText: '#FFFFFF',
    topbarBg: '#FFFFFF', topbarText: '#37474F',
    buttonPrimary: '#66BB6A', buttonSecondary: '#37474F', buttonText: '#FFFFFF',
    listingAccent: '#2E7D32', pageBg: '#F3FBF3',
  },
  {
    id: 'purple', label: 'Royal Purple',
    menuBg: '#6A1B9A', menuText: '#FFFFFF',
    topbarBg: '#FFFFFF', topbarText: '#37474F',
    buttonPrimary: '#AB47BC', buttonSecondary: '#37474F', buttonText: '#FFFFFF',
    listingAccent: '#6A1B9A', pageBg: '#FAF3FC',
  },
  {
    id: 'slate', label: 'Slate Dark',
    menuBg: '#37474F', menuText: '#FFFFFF',
    topbarBg: '#FFFFFF', topbarText: '#37474F',
    buttonPrimary: '#78909C', buttonSecondary: '#546E7A', buttonText: '#FFFFFF',
    listingAccent: '#37474F', pageBg: '#F4F6F7',
  },
];

// ── Tiny demo building blocks (menu items, listing rows, buttons) ───────────
// used to compose the desktop/mobile mockups below. Deliberately abstract —
// short bars/dots standing in for real text/icons at this scale.

function MenuItem({ tone }) {
  return (
    <div style={mock.menuItem}>
      <span style={{ ...mock.menuDot, background: tone }} />
      <span style={{ ...mock.menuLine, background: tone }} />
    </div>
  );
}

function NavPill({ tone, opacity = 1 }) {
  return <span style={{ ...mock.navPill, background: tone, opacity }} />;
}

function IconDot({ size = 6, tone, opacity = 1 }) {
  return <span style={{ ...mock.iconDot, width: size, height: size, background: tone, opacity }} />;
}

function ActionBtn({ color, textColor, wide }) {
  return (
    <span style={{ ...mock.actionBtn, background: color, ...(wide ? { flex: 1 } : {}) }}>
      <span style={{ ...mock.actionBtnPlus, color: textColor }}>+</span>
    </span>
  );
}

// Outline/ghost button — demonstrates the secondary button color alongside
// the filled primary button in the listing header.
function OutlineBtn({ color }) {
  return <span style={{ ...mock.outlineBtn, borderColor: color, color }}>−</span>;
}

function ListRow({ compact, tone }) {
  return (
    <div style={{ ...mock.listRow, padding: compact ? '3px 0' : '4px 0' }}>
      <span style={{ ...mock.listRowThumb, background: tone, opacity: 0.55 }} />
      <span style={mock.listRowLines}>
        <span style={{ ...mock.listRowLine, background: tone, opacity: 0.65, width: '70%' }} />
        {!compact && <span style={{ ...mock.listRowLine, background: tone, opacity: 0.35, width: '45%' }} />}
      </span>
    </div>
  );
}

function Listing({ colors, rows = 3, compact }) {
  return (
    <div style={{ ...mock.content, background: colors.pageBg }}>
      <div style={mock.contentHeader}>
        <span style={{ ...mock.contentTitle, background: colors.listingAccent, opacity: 0.6 }} />
        <div style={mock.contentBtnGroup}>
          {!compact && <OutlineBtn color={colors.buttonSecondary} />}
          <ActionBtn color={colors.buttonPrimary} textColor={colors.buttonText} />
        </div>
      </div>
      {Array.from({ length: rows }).map((_, i) => <ListRow key={i} compact={compact} tone={colors.listingAccent} />)}
    </div>
  );
}

// ── Desktop / mobile mockups per layout ──────────────────────────────────────

function DesktopMock({ layoutId, colors }) {
  if (layoutId === 'topbar-only') {
    return (
      <div style={mock.frameDesktop}>
        <div style={{ ...mock.topbarWide, background: colors.menuBg }}>
          <div style={mock.topbarLeft}>
            <IconDot size={7} tone={colors.menuText} />
            <NavPill tone={colors.menuText} opacity={0.55} />
            <NavPill tone={colors.menuText} opacity={0.35} />
            <NavPill tone={colors.menuText} opacity={0.35} />
          </div>
          <IconDot size={7} tone={colors.menuText} opacity={0.9} />
        </div>
        <Listing colors={colors} />
      </div>
    );
  }
  if (layoutId === 'compact-sidebar') {
    return (
      <div style={mock.frameDesktop}>
        <div style={{ ...mock.railThin, background: colors.menuBg }}>
          {[0, 1, 2, 3].map(i => <IconDot key={i} size={6} tone={colors.menuText} opacity={0.85} />)}
        </div>
        <Listing colors={colors} rows={3} />
      </div>
    );
  }
  // sidebar-topbar (default)
  return (
    <div style={mock.frameDesktop}>
      <div style={{ ...mock.railWide, background: colors.menuBg }}>
        {['a', 'b', 'c', 'd'].map(k => <MenuItem key={k} tone={colors.menuText} />)}
      </div>
      <div style={mock.mainCol}>
        <div style={{ ...mock.topbarSlim, background: colors.topbarBg }}>
          <span style={{ ...mock.topbarSearch, background: colors.topbarText, opacity: 0.35 }} />
          <IconDot size={7} tone={colors.buttonPrimary} />
        </div>
        <Listing colors={colors} />
      </div>
    </div>
  );
}

function MobileMock({ layoutId, colors }) {
  if (layoutId === 'topbar-only') {
    return (
      <div style={mock.frameMobile}>
        <span style={mock.notch} />
        <div style={{ ...mock.topbarMobile, background: colors.menuBg }}>
          <IconDot size={6} tone={colors.menuText} />
          <IconDot size={6} tone={colors.menuText} opacity={0.9} />
        </div>
        <Listing colors={colors} rows={2} compact />
        <div style={{ ...mock.bottomTabs, background: colors.topbarBg }}>
          {[0, 1, 2, 3].map(i => (
            <IconDot key={i} size={6} tone={i === 0 ? colors.buttonPrimary : colors.topbarText} opacity={i === 0 ? 1 : 0.35} />
          ))}
        </div>
      </div>
    );
  }
  if (layoutId === 'compact-sidebar') {
    return (
      <div style={mock.frameMobile}>
        <span style={mock.notch} />
        <span style={{ ...mock.fabMenu, background: colors.menuBg }}>
          <span style={{ ...mock.fabBar, background: colors.menuText }} />
          <span style={{ ...mock.fabBar, background: colors.menuText }} />
          <span style={{ ...mock.fabBar, background: colors.menuText }} />
        </span>
        <div style={mock.mobileContentPad}>
          <Listing colors={colors} rows={3} compact />
        </div>
      </div>
    );
  }
  // sidebar-topbar (default) — sidebar collapses behind a hamburger on mobile
  return (
    <div style={mock.frameMobile}>
      <span style={mock.notch} />
      <div style={{ ...mock.topbarMobile, background: colors.menuBg }}>
        <span style={mock.hamburger}>
          <span style={{ ...mock.hamburgerBar, background: colors.menuText }} />
          <span style={{ ...mock.hamburgerBar, background: colors.menuText }} />
          <span style={{ ...mock.hamburgerBar, background: colors.menuText }} />
        </span>
        <IconDot size={6} tone={colors.menuText} opacity={0.9} />
      </div>
      <Listing colors={colors} rows={2} compact />
      <div style={{ ...mock.bottomAction, background: colors.topbarBg }}>
        <ActionBtn color={colors.buttonPrimary} textColor={colors.buttonText} wide />
      </div>
    </div>
  );
}

const HEX_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

function toPickerHex(v) {
  return HEX_RE.test(v) && v.length === 7 ? v : '#000000';
}

// Text-entry-or-picker field for one custom color role. Keeps its own draft
// text so the user can type freely; only commits (and updates the live
// preview) once the value is a valid hex code.
function ColorField({ label, value, onChange, onReset, isOverridden }) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);

  function commit(v) {
    setText(v);
    if (HEX_RE.test(v)) onChange(v);
  }

  return (
    <div style={styles.colorFieldRow}>
      <div style={styles.colorFieldLabelCol}>
        <span style={styles.colorFieldLabel}>{label}</span>
        {isOverridden && (
          <button type="button" style={styles.colorFieldResetLink} onClick={onReset}>Reset</button>
        )}
      </div>
      <div style={styles.colorFieldInputWrap}>
        <input
          type="color"
          value={toPickerHex(text)}
          onChange={e => commit(e.target.value)}
          style={styles.colorFieldSwatch}
          title="Open color picker"
        />
        <input
          type="text"
          value={text}
          onChange={e => commit(e.target.value)}
          placeholder="#RRGGBB"
          style={styles.colorFieldText}
          spellCheck={false}
          maxLength={7}
        />
      </div>
    </div>
  );
}

// ── "Big" mockups — used only for the large "Selected layout" preview in
// Step 2, with real dummy menu labels (Menu 1, Menu 2 …) instead of abstract
// bars, so it reads at a glance instead of needing to be decoded. ───────────

function MenuItemBig({ label, tone, active }) {
  return (
    <div style={{ ...mock.menuItemBig, ...(active ? { background: 'rgba(255,255,255,0.18)' } : {}) }}>
      <span style={{ ...mock.menuIconBig, background: tone, opacity: 0.85 }} />
      <span style={{ ...mock.menuLabelBig, color: tone, opacity: active ? 1 : 0.75, fontWeight: active ? 700 : 500 }}>
        {label}
      </span>
    </div>
  );
}

function NavLabelBig({ label, tone, opacity = 1 }) {
  return <span style={{ ...mock.navLabelBig, color: tone, opacity }}>{label}</span>;
}

function ActionBtnBig({ color, textColor, label }) {
  return (
    <span style={{ ...mock.actionBtnBig, background: color }}>
      <span style={{ color: textColor }}>{label}</span>
    </span>
  );
}

function OutlineBtnBig({ color, label }) {
  return <span style={{ ...mock.outlineBtnBig, borderColor: color, color }}>{label}</span>;
}

function ListRowBig({ tone }) {
  return (
    <div style={mock.listRowBig}>
      <span style={{ ...mock.listRowThumbBig, background: tone, opacity: 0.5 }} />
      <span style={mock.listRowLinesBig}>
        <span style={{ ...mock.listRowLineBig, background: tone, opacity: 0.65, width: '75%' }} />
        <span style={{ ...mock.listRowLineBig, background: tone, opacity: 0.35, width: '48%' }} />
      </span>
    </div>
  );
}

function ListingBig({ colors, rows = 3, compact, hideActions }) {
  return (
    <div style={{ ...mock.contentBig, background: colors.pageBg }}>
      <div style={mock.contentHeaderBig}>
        <span style={{ ...mock.contentTitleBig, background: colors.listingAccent, opacity: 0.6, ...(compact ? { width: '30%' } : {}) }} />
        {!hideActions && (
          <div style={mock.contentBtnGroupBig}>
            {!compact && <OutlineBtnBig color={colors.buttonSecondary} label="Cancel" />}
            <ActionBtnBig color={colors.buttonPrimary} textColor={colors.buttonText} label="+ New" />
          </div>
        )}
      </div>
      {Array.from({ length: rows }).map((_, i) => <ListRowBig key={i} tone={colors.listingAccent} />)}
    </div>
  );
}

const MENU_LABELS = ['Menu 1', 'Menu 2', 'Menu 3', 'Menu 4'];

function DesktopMockBig({ layoutId, colors }) {
  if (layoutId === 'topbar-only') {
    return (
      <div style={{ ...mock.frameDesktopBig, flexDirection: 'column' }}>
        <div style={{ ...mock.topbarWideBig, background: colors.menuBg }}>
          <div style={mock.topbarLeftBig}>
            <IconDot size={12} tone={colors.menuText} />
            {MENU_LABELS.slice(0, 3).map((l, i) => (
              <NavLabelBig key={l} label={l} tone={colors.menuText} opacity={i === 0 ? 1 : 0.7} />
            ))}
          </div>
          <IconDot size={12} tone={colors.menuText} opacity={0.9} />
        </div>
        <ListingBig colors={colors} />
      </div>
    );
  }
  if (layoutId === 'compact-sidebar') {
    return (
      <div style={mock.frameDesktopBig}>
        <div style={{ ...mock.railThinBig, background: colors.menuBg }}>
          {[0, 1, 2, 3].map(i => <IconDot key={i} size={16} tone={colors.menuText} opacity={i === 0 ? 1 : 0.7} />)}
        </div>
        <ListingBig colors={colors} />
      </div>
    );
  }
  // sidebar-topbar (default)
  return (
    <div style={mock.frameDesktopBig}>
      <div style={{ ...mock.railWideBig, background: colors.menuBg }}>
        {MENU_LABELS.map((label, i) => (
          <MenuItemBig key={label} label={label} tone={colors.menuText} active={i === 0} />
        ))}
      </div>
      <div style={mock.mainColBig}>
        <div style={{ ...mock.topbarSlimBig, background: colors.topbarBg }}>
          <span style={{ ...mock.topbarSearchBig, background: colors.topbarText, opacity: 0.3 }} />
          <IconDot size={12} tone={colors.buttonPrimary} />
        </div>
        <ListingBig colors={colors} />
      </div>
    </div>
  );
}

function MobileMockBig({ layoutId, colors }) {
  if (layoutId === 'topbar-only') {
    return (
      <div style={mock.frameMobileBig}>
        <span style={mock.notchBig} />
        <div style={{ ...mock.topbarMobileBig, background: colors.menuBg }}>
          <IconDot size={10} tone={colors.menuText} />
          <IconDot size={10} tone={colors.menuText} opacity={0.9} />
        </div>
        <ListingBig colors={colors} rows={2} compact />
        <div style={{ ...mock.bottomTabsBig, background: colors.topbarBg }}>
          {[0, 1, 2, 3].map(i => (
            <IconDot key={i} size={11} tone={i === 0 ? colors.buttonPrimary : colors.topbarText} opacity={i === 0 ? 1 : 0.35} />
          ))}
        </div>
      </div>
    );
  }
  if (layoutId === 'compact-sidebar') {
    return (
      <div style={mock.frameMobileBig}>
        <span style={mock.notchBig} />
        <span style={{ ...mock.fabMenuBig, background: colors.menuBg }}>
          <span style={{ ...mock.fabBarBig, background: colors.menuText }} />
          <span style={{ ...mock.fabBarBig, background: colors.menuText }} />
          <span style={{ ...mock.fabBarBig, background: colors.menuText }} />
        </span>
        <div style={mock.mobileContentPadBig}>
          <ListingBig colors={colors} rows={2} compact />
        </div>
      </div>
    );
  }
  return (
    <div style={mock.frameMobileBig}>
      <span style={mock.notchBig} />
      <div style={{ ...mock.topbarMobileBig, background: colors.menuBg }}>
        <span style={mock.hamburgerBig}>
          <span style={{ ...mock.hamburgerBarBig, background: colors.menuText }} />
          <span style={{ ...mock.hamburgerBarBig, background: colors.menuText }} />
          <span style={{ ...mock.hamburgerBarBig, background: colors.menuText }} />
        </span>
        <IconDot size={10} tone={colors.menuText} opacity={0.9} />
      </div>
      <ListingBig colors={colors} rows={2} compact hideActions />
      <div style={{ ...mock.bottomActionBig, background: colors.topbarBg }}>
        <ActionBtnBig color={colors.buttonPrimary} textColor={colors.buttonText} label="+ New" />
      </div>
    </div>
  );
}

function ResponsivePreviewBig({ layoutId, colors }) {
  return (
    <div style={mock.previewRowBig}>
      <div style={mock.previewCol}>
        <DesktopMockBig layoutId={layoutId} colors={colors} />
        <span style={mock.viewportCaption}>🖥 Desktop</span>
      </div>
      <div style={mock.previewCol}>
        <MobileMockBig layoutId={layoutId} colors={colors} />
        <span style={mock.viewportCaption}>📱 Mobile</span>
      </div>
    </div>
  );
}

function ResponsivePreview({ layoutId, colors }) {
  return (
    <div style={mock.previewRow}>
      <div style={mock.previewCol}>
        <DesktopMock layoutId={layoutId} colors={colors} />
        <span style={mock.viewportCaption}>🖥 Desktop</span>
      </div>
      <div style={mock.previewCol}>
        <MobileMock layoutId={layoutId} colors={colors} />
        <span style={mock.viewportCaption}>📱 Mobile</span>
      </div>
    </div>
  );
}

// ── Layout + responsive CSS blocks, one per layout choice ───────────────────
// Each defines the same three structural classes — .app-shell, .app-nav
// (the colored menu/topbar chrome) and .app-main (the page content) — plus
// whatever markup that layout additionally needs (overlay, bottom tabs, FAB),
// so switching MOBILE_BREAKPOINT in one place moves every rule together.

const MOBILE_BREAKPOINT = 768;

function sidebarTopbarLayoutCss() {
  return `/* Sidebar + Topbar — collapsible left nav with a slim top bar */

.app-shell {
  display: flex;
  min-height: 100vh;
  background: var(--page-bg);
}

.app-nav {
  width: 220px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 10px;
  background: var(--menu-bg);
  color: var(--menu-text);
  transition: transform 0.25s ease;
}

.app-nav-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  color: var(--menu-text);
  text-decoration: none;
  opacity: 0.85;
}

.app-nav-link.active,
.app-nav-link:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.15);
}

.app-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.app-topbar {
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: var(--topbar-bg);
  color: var(--topbar-text);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.app-main {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

/* Hamburger — hidden on desktop, shown once the sidebar goes off-canvas */
.app-menu-toggle {
  display: none;
  background: none;
  border: none;
  color: var(--topbar-text);
  cursor: pointer;
}

.app-nav-overlay {
  display: none;
}

@media (max-width: ${MOBILE_BREAKPOINT}px) {
  .app-nav {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 50;
    transform: translateX(-100%);
    box-shadow: 2px 0 12px rgba(0, 0, 0, 0.15);
  }

  .app-nav.is-open {
    transform: translateX(0);
  }

  .app-menu-toggle {
    display: inline-flex;
  }

  .app-nav-overlay.is-open {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 40;
    background: rgba(0, 0, 0, 0.4);
  }

  .app-main {
    padding: 16px;
  }
}
`;
}

function topbarOnlyLayoutCss() {
  return `/* Topbar Only — full-width top nav; collapses to a bottom tab bar on mobile */

.app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--page-bg);
}

.app-topbar {
  height: 60px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: var(--menu-bg);
  color: var(--menu-text);
}

.app-topbar-nav {
  display: flex;
  align-items: center;
  gap: 24px;
}

.app-nav-link {
  color: var(--menu-text);
  text-decoration: none;
  font-weight: 600;
  opacity: 0.8;
}

.app-nav-link.active,
.app-nav-link:hover {
  opacity: 1;
}

.app-main {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

/* Bottom tab bar — hidden on desktop, shown in place of the top nav links */
.app-bottom-tabs {
  display: none;
}

@media (max-width: ${MOBILE_BREAKPOINT}px) {
  .app-topbar-nav {
    display: none;
  }

  .app-bottom-tabs {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 50;
    height: 56px;
    align-items: center;
    justify-content: space-around;
    background: var(--topbar-bg);
    color: var(--topbar-text);
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .app-bottom-tabs .app-nav-link {
    color: var(--topbar-text);
  }

  .app-main {
    padding: 16px 16px 72px;
  }
}
`;
}

function compactSidebarLayoutCss() {
  return `/* Compact Sidebar — icon-only rail; hides behind a floating button on mobile */

.app-shell {
  display: flex;
  min-height: 100vh;
  background: var(--page-bg);
}

.app-nav {
  width: 64px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding-top: 16px;
  background: var(--menu-bg);
}

.app-nav-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  color: var(--menu-text);
  text-decoration: none;
  opacity: 0.8;
}

.app-nav-link.active,
.app-nav-link:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.15);
}

.app-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.app-main {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

/* Floating menu button — replaces the icon rail once it's hidden on mobile */
.app-fab-menu {
  display: none;
}

@media (max-width: ${MOBILE_BREAKPOINT}px) {
  .app-nav {
    display: none;
  }

  .app-fab-menu {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 16px;
    left: 16px;
    z-index: 50;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: var(--menu-bg);
    color: var(--menu-text);
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  }

  .app-main {
    padding: 16px;
  }
}
`;
}

const LAYOUT_CSS_BUILDERS = {
  'sidebar-topbar':   sidebarTopbarLayoutCss,
  'topbar-only':      topbarOnlyLayoutCss,
  'compact-sidebar':  compactSidebarLayoutCss,
};

function sharedComponentsCss() {
  return `/* Buttons */

.btn-primary {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: var(--button-primary);
  color: var(--button-text);
  font-weight: 600;
  cursor: pointer;
}

.btn-secondary {
  padding: 10px 20px;
  border: 1.5px solid var(--button-secondary);
  border-radius: 8px;
  background: transparent;
  color: var(--button-secondary);
  font-weight: 600;
  cursor: pointer;
}

/* Listing */

.listing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.listing-title {
  color: var(--listing-accent);
  font-weight: 700;
}

.listing-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.listing-row .thumb {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  flex-shrink: 0;
  background: var(--listing-accent);
  opacity: 0.15;
}
`;
}

// Builds a drop-in theme.css for the generated React app — from the wizard's
// chosen layout + color roles. Includes the color variables, the layout's
// structural classes, its responsive (mobile) breakpoint, and shared
// button/listing styles.
function buildThemeCss({ layout, layoutLabel, colors }) {
  const layoutCss = (LAYOUT_CSS_BUILDERS[layout] || sidebarTopbarLayoutCss)();

  return `/* ============================================================
   ENABLE APP — THEME.CSS
   Generated by the Theme wizard — Layout: ${layoutLabel}
   Drop this file into your React project and import it once in
   your root index.js / main.jsx. All classes below are ready-to-use;
   these CSS variables let you tweak the palette in one place.
   Responsive: layout switches to its mobile styles at ${MOBILE_BREAKPOINT}px.
   ============================================================ */

:root {
  /* Layout */
  --app-layout: "${layout}";

  /* Menu / navigation */
  --menu-bg: ${colors.menuBg};
  --menu-text: ${colors.menuText};

  /* Topbar */
  --topbar-bg: ${colors.topbarBg};
  --topbar-text: ${colors.topbarText};

  /* Buttons */
  --button-primary: ${colors.buttonPrimary};
  --button-secondary: ${colors.buttonSecondary};
  --button-text: ${colors.buttonText};

  /* Content / listing */
  --listing-accent: ${colors.listingAccent};
  --page-bg: ${colors.pageBg};
}

/* ============================================================
   LAYOUT — ${layoutLabel}
   ============================================================ */

${layoutCss}
/* ============================================================
   SHARED COMPONENTS
   ============================================================ */

${sharedComponentsCss()}`;
}

function downloadTextFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ThemePage() {
  const [step,      setStep]      = useState(1);
  const [layout,    setLayout]    = useState('sidebar-topbar');
  const [scheme,    setScheme]    = useState('orange');
  // Custom color overrides keyed by COLOR_FIELDS key — a key present here
  // wins over the active preset's value for that role.
  const [overrides, setOverrides] = useState({});
  const [generated, setGenerated] = useState(false);

  const activeScheme = useMemo(
    () => COLOR_SCHEMES.find(c => c.id === scheme) || COLOR_SCHEMES[0],
    [scheme]
  );

  // Preset colors with any custom text-field/picker overrides applied — this
  // is what actually gets rendered in the previews and in the exported CSS.
  const effectiveColors = useMemo(() => {
    const result = {};
    for (const f of COLOR_FIELDS) result[f.key] = overrides[f.key] || activeScheme[f.key];
    return result;
  }, [activeScheme, overrides]);

  const hasOverrides = Object.keys(overrides).length > 0;

  function selectPreset(id) {
    setScheme(id);
    setOverrides({});
  }

  function setOverride(key, value) {
    setOverrides(prev => ({ ...prev, [key]: value }));
  }

  function clearOverride(key) {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function handleGenerateTheme() {
    const css = buildThemeCss({
      layout, layoutLabel: LAYOUTS.find(l => l.id === layout)?.label ?? layout,
      colors: effectiveColors,
    });
    downloadTextFile('theme.css', css, 'text/css');
    setGenerated(true);
  }

  function handleEditAgain() {
    setGenerated(false);
    setStep(1);
  }

  if (generated) {
    return (
      <div style={styles.page}>
        <div style={styles.doneCard}>
          <span style={styles.doneIcon}>✅</span>
          <h2 style={styles.doneTitle}>Theme generated</h2>
          <p style={styles.doneSubtitle}>
            Layout: <strong>{LAYOUTS.find(l => l.id === layout)?.label}</strong> — Colors: <strong>{hasOverrides ? 'Custom' : activeScheme.label}</strong>
          </p>
          <p style={styles.doneSubtitle}>theme.css has been downloaded.</p>
          <button style={styles.editAgainBtn} onClick={handleEditAgain}>Edit Preferences</button>
        </div>
      </div>
    );
  }

  const groupedFields = COLOR_FIELDS.reduce((acc, f) => {
    (acc[f.group] ||= []).push(f);
    return acc;
  }, {});

  return (
    <div style={styles.wizard}>
      {/* ── Step progress bar ── */}
      <div style={styles.stepBar}>
        <div style={styles.stepTrack}>
          {WIZARD_STEPS.map((s, i) => {
            const done   = step > s.id;
            const active = step === s.id;
            return [
              <div key={`step-${s.id}`} style={styles.stepItem}>
                <div style={{ ...styles.stepCircle, ...(active ? styles.stepCircleActive : done ? styles.stepCircleDone : {}) }}>
                  {done ? '✓' : s.id}
                </div>
                <span style={{ ...styles.stepLabel, ...(active ? styles.stepLabelActive : done ? styles.stepLabelDone : {}) }}>
                  {s.label}
                </span>
              </div>,
              i < WIZARD_STEPS.length - 1
                ? <div key={`line-${s.id}`} style={{ ...styles.stepConnector, ...(done ? styles.stepConnectorDone : {}) }} />
                : null,
            ];
          })}
        </div>
      </div>

      <div style={styles.stepBody}>
        {step === 1 && (
          <>
            <div style={styles.stepBanner}>
              <div style={styles.stepBannerNum}>1</div>
              <div style={styles.stepBannerText}>
                <span style={styles.stepBannerTitle}>Choose a layout</span>
                <span style={styles.stepBannerSub}>Pick the app shell for the generated React application — with a live look at menu, listing & buttons on desktop and mobile.</span>
              </div>
            </div>

            <div style={styles.grid}>
              {LAYOUTS.map(l => {
                const active = layout === l.id;
                return (
                  <button
                    key={l.id}
                    type="button"
                    style={{ ...styles.card, ...(active ? styles.cardActive : {}) }}
                    onClick={() => setLayout(l.id)}
                  >
                    <ResponsivePreview layoutId={l.id} colors={effectiveColors} />
                    <div style={styles.cardLabelRow}>
                      <span style={styles.cardLabel}>{l.label}</span>
                      {active && <span style={styles.cardCheck}>✓</span>}
                    </div>
                    <p style={styles.cardDesc}>{l.description}</p>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div style={styles.stepBanner}>
              <div style={styles.stepBannerNum}>2</div>
              <div style={styles.stepBannerText}>
                <span style={styles.stepBannerTitle}>Choose a color scheme</span>
                <span style={styles.stepBannerSub}>Pick a preset palette, or enter your own colors below.</span>
              </div>
            </div>

            {/* ── Live preview of the layout chosen in Step 1 ── */}
            <div style={styles.livePreviewSection}>
              <div style={styles.livePreviewHeader}>
                <span style={styles.livePreviewLabel}>Selected layout</span>
                <span style={styles.livePreviewLayoutName}>{LAYOUTS.find(l => l.id === layout)?.label}</span>
              </div>
              <ResponsivePreviewBig layoutId={layout} colors={effectiveColors} />
            </div>

            {/* ── Preset palettes ── */}
            <div style={styles.sectionTitle}>Preset palettes</div>
            <div style={styles.presetRow}>
              {COLOR_SCHEMES.map(c => {
                const active = scheme === c.id && !hasOverrides;
                return (
                  <button
                    key={c.id}
                    type="button"
                    style={{ ...styles.presetCard, ...(active ? styles.presetCardActive : {}) }}
                    onClick={() => selectPreset(c.id)}
                  >
                    <div style={styles.swatchRow}>
                      <span style={{ ...styles.swatch, background: c.menuBg }} />
                      <span style={{ ...styles.swatch, background: c.buttonPrimary }} />
                      <span style={{ ...styles.swatch, background: c.buttonSecondary }} />
                      <span style={{ ...styles.swatch, background: c.pageBg, border: '1px solid var(--border)' }} />
                    </div>
                    <span style={styles.presetLabel}>{c.label}</span>
                    {active && <span style={styles.cardCheck}>✓</span>}
                  </button>
                );
              })}
            </div>

            {/* ── Custom colors ── */}
            <div style={styles.customSection}>
              <div style={styles.customSectionHeader}>
                <span style={styles.sectionTitle}>Or enter custom colors</span>
                {hasOverrides && (
                  <button type="button" style={styles.colorFieldResetLink} onClick={() => setOverrides({})}>
                    Reset all to preset
                  </button>
                )}
              </div>

              {Object.entries(groupedFields).map(([group, fields]) => (
                <div key={group} style={styles.customGroup}>
                  <span style={styles.customGroupLabel}>{group}</span>
                  {fields.map(f => (
                    <ColorField
                      key={f.key}
                      label={f.label}
                      value={effectiveColors[f.key]}
                      isOverridden={!!overrides[f.key]}
                      onChange={v => setOverride(f.key, v)}
                      onReset={() => clearOverride(f.key)}
                    />
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Footer navigation ── */}
      <div style={styles.footer}>
        <span style={styles.footerHint}>
          {step === 1 ? 'Select a layout to continue.' : 'Select a color scheme, then generate your theme.'}
        </span>
        {step === 2 && (
          <button style={styles.backBtn} onClick={() => setStep(1)}>
            ← Back
          </button>
        )}
        {step === 1 ? (
          <button style={styles.nextBtn} onClick={() => setStep(2)}>
            Next →
          </button>
        ) : (
          <button style={styles.nextBtn} onClick={handleGenerateTheme}>
            ⚙ Generate Theme
          </button>
        )}
      </div>
    </div>
  );
}

// ── Mockup styles (desktop/mobile demo previews) ─────────────────────────────

const mock = {
  previewRow: { display: 'flex', gap: 10, alignItems: 'flex-start' },
  previewCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0 },
  viewportCaption: { fontSize: 10, fontWeight: 600, color: 'var(--text-muted)' },

  frameDesktop: {
    display: 'flex', width: 168, height: 118, borderRadius: 8,
    border: '1px solid var(--border)', overflow: 'hidden', flexShrink: 0,
  },
  frameMobile: {
    display: 'flex', flexDirection: 'column', width: 62, height: 118, borderRadius: 12,
    border: '2px solid var(--border-strong)', overflow: 'hidden', flexShrink: 0, position: 'relative',
  },
  notch: {
    position: 'absolute', top: 3, left: '50%', transform: 'translateX(-50%)',
    width: 16, height: 3, borderRadius: 2, background: 'var(--border-strong)', zIndex: 2,
  },

  railWide: { width: 34, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 7, padding: '8px 5px' },
  railThin: { width: 16, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '8px 0' },
  mainCol: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  topbarSlim: {
    height: 14, flexShrink: 0, background: '#fff', borderBottom: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 6px',
  },
  topbarSearch: { height: 4, width: '45%', borderRadius: 2, background: 'rgba(0,0,0,0.1)' },
  topbarWide: {
    height: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px',
  },
  topbarLeft: { display: 'flex', alignItems: 'center', gap: 5 },

  menuItem: { display: 'flex', alignItems: 'center', gap: 4 },
  menuDot: { width: 5, height: 5, borderRadius: 2, flexShrink: 0, opacity: 0.95 },
  menuLine: { height: 3, width: 16, borderRadius: 2, opacity: 0.55 },
  navPill: { width: 14, height: 6, borderRadius: 3, flexShrink: 0 },
  iconDot: { borderRadius: '50%', flexShrink: 0, display: 'inline-block' },

  content: { flex: 1, display: 'flex', flexDirection: 'column', gap: 5, padding: 8, minWidth: 0 },
  contentHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  contentTitle: { height: 6, width: '40%', borderRadius: 3 },
  contentBtnGroup: { display: 'flex', alignItems: 'center', gap: 4 },
  actionBtn: {
    width: 14, height: 10, borderRadius: 3, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  actionBtnPlus: { color: '#fff', fontSize: 8, fontWeight: 800, lineHeight: 1 },
  outlineBtn: {
    width: 14, height: 10, borderRadius: 3, flexShrink: 0, border: '1.3px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 8, fontWeight: 800, lineHeight: 1, background: 'transparent',
  },

  listRow: { display: 'flex', alignItems: 'center', gap: 5, borderBottom: '1px solid rgba(0,0,0,0.06)' },
  listRowThumb: { width: 8, height: 8, borderRadius: 2, flexShrink: 0 },
  listRowLines: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  listRowLine: { height: 3, borderRadius: 2 },

  topbarMobile: {
    height: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 6px 0 8px',
  },
  hamburger: { display: 'flex', flexDirection: 'column', gap: 2, width: 9 },
  hamburgerBar: { height: 1.4, width: '100%', borderRadius: 1 },
  bottomTabs: {
    height: 16, flexShrink: 0, borderTop: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-around',
  },
  bottomAction: {
    height: 18, flexShrink: 0, borderTop: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 10px',
  },
  mobileContentPad: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  fabMenu: {
    position: 'absolute', top: 8, left: 6, width: 14, height: 14, borderRadius: '50%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5,
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)', zIndex: 2,
  },
  fabBar: { width: 6, height: 1.2, borderRadius: 1 },

  // ── "Big" variants for the large Step 2 "Selected layout" preview ──
  previewRowBig: { display: 'flex', gap: 24, alignItems: 'flex-start', justifyContent: 'center' },

  frameDesktopBig: {
    display: 'flex', width: 340, height: 210, borderRadius: 10,
    border: '1px solid var(--border)', overflow: 'hidden', flexShrink: 0,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  frameMobileBig: {
    display: 'flex', flexDirection: 'column', width: 132, height: 210, borderRadius: 18,
    border: '4px solid var(--border-strong)', overflow: 'hidden', flexShrink: 0, position: 'relative',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  notchBig: {
    position: 'absolute', top: 5, left: '50%', transform: 'translateX(-50%)',
    width: 34, height: 5, borderRadius: 3, background: 'var(--border-strong)', zIndex: 2,
  },

  railWideBig: { width: 108, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, padding: '14px 8px' },
  railThinBig: { width: 40, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '16px 0' },
  mainColBig: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  topbarSlimBig: {
    height: 30, flexShrink: 0, borderBottom: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px',
  },
  topbarSearchBig: { height: 8, width: '45%', borderRadius: 4 },
  topbarWideBig: {
    height: 34, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px',
  },
  topbarLeftBig: { display: 'flex', alignItems: 'center', gap: 16 },

  menuItemBig: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6 },
  menuIconBig: { width: 14, height: 14, borderRadius: 4, flexShrink: 0 },
  menuLabelBig: { fontSize: 12, whiteSpace: 'nowrap' },
  navLabelBig: { fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },

  contentBig: { flex: 1, display: 'flex', flexDirection: 'column', gap: 10, padding: 16, minWidth: 0 },
  contentHeaderBig: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  contentTitleBig: { height: 10, width: '35%', borderRadius: 4 },
  contentBtnGroupBig: { display: 'flex', alignItems: 'center', gap: 8 },
  actionBtnBig: {
    padding: '5px 12px', borderRadius: 6, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
  },
  outlineBtnBig: {
    padding: '5px 12px', borderRadius: 6, flexShrink: 0, border: '1.5px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700, background: 'transparent', whiteSpace: 'nowrap',
  },

  listRowBig: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' },
  listRowThumbBig: { width: 18, height: 18, borderRadius: 5, flexShrink: 0 },
  listRowLinesBig: { flex: 1, display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 },
  listRowLineBig: { height: 6, borderRadius: 3 },

  topbarMobileBig: {
    height: 30, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 12px 0 14px',
  },
  hamburgerBig: { display: 'flex', flexDirection: 'column', gap: 4, width: 18 },
  hamburgerBarBig: { height: 2.4, width: '100%', borderRadius: 1 },
  bottomTabsBig: {
    height: 32, flexShrink: 0, borderTop: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-around',
  },
  bottomActionBig: {
    height: 36, flexShrink: 0, borderTop: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 14px',
  },
  mobileContentPadBig: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  fabMenuBig: {
    position: 'absolute', top: 14, left: 10, width: 26, height: 26, borderRadius: '50%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
    boxShadow: '0 1px 4px rgba(0,0,0,0.3)', zIndex: 2,
  },
  fabBarBig: { width: 12, height: 2, borderRadius: 1 },
};

// ── Page / wizard chrome styles ───────────────────────────────────────────────

const styles = {
  page: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100%', background: 'var(--bg-page)',
  },

  doneCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    padding: '48px 56px', background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-xs)', textAlign: 'center',
  },
  doneIcon: { fontSize: 32 },
  doneTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' },
  doneSubtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },
  editAgainBtn: {
    marginTop: 12, padding: '10px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8,
  },

  wizard: {
    display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
    background: 'var(--bg-page)',
  },

  stepBar: { flexShrink: 0, background: '#fff', borderBottom: '1px solid var(--border)', padding: '0 32px' },
  stepTrack: { display: 'flex', alignItems: 'center', maxWidth: 400, margin: '0 auto', height: 72 },
  stepItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0, position: 'relative' },
  stepCircle: {
    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, fontWeight: 800, background: '#fff', border: '2px solid var(--border)',
    color: 'var(--text-muted)', boxSizing: 'border-box',
  },
  stepCircleActive: { background: 'var(--primary)', borderColor: 'var(--primary)', color: '#fff', boxShadow: '0 0 0 4px rgba(230,81,0,0.12)' },
  stepCircleDone: { background: '#1B7F3B', borderColor: '#1B7F3B', color: '#fff' },
  stepLabel: {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase',
    color: 'var(--text-muted)', whiteSpace: 'nowrap', lineHeight: 1,
  },
  stepLabelActive: { color: 'var(--primary)' },
  stepLabelDone: { color: '#1B7F3B' },
  stepConnector: { flex: 1, height: 2, background: 'var(--border)', margin: '0 12px', marginBottom: 22 },
  stepConnectorDone: { background: '#1B7F3B' },

  stepBody: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 28px 28px' },
  stepBanner: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '16px 0', flexShrink: 0,
    borderBottom: '1px solid var(--border)', marginBottom: 20,
  },
  stepBannerNum: {
    width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: 'var(--primary)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800,
  },
  stepBannerText: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  stepBannerTitle: { fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.2 },
  stepBannerSub: { fontSize: 12, color: 'var(--text-muted)' },

  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16,
  },
  card: {
    display: 'flex', flexDirection: 'column', gap: 10, padding: 16, textAlign: 'left',
    background: '#fff', border: '2px solid var(--border)', borderRadius: 12, cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)', transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  cardActive: { borderColor: 'var(--primary)', boxShadow: '0 6px 20px rgba(230,81,0,0.14)' },
  cardLabelRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardLabel: { fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' },
  cardCheck: { fontSize: 13, fontWeight: 800, color: 'var(--primary)' },
  cardDesc: { margin: 0, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 },

  swatchRow: { display: 'flex', gap: 5 },
  swatch: { width: 16, height: 16, borderRadius: '50%', flexShrink: 0 },

  sectionTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' },

  livePreviewSection: {
    background: '#fff', border: '1px solid var(--border)', borderRadius: 12,
    padding: '28px 20px', display: 'flex', flexDirection: 'column', gap: 18, alignItems: 'center',
  },
  livePreviewHeader: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  livePreviewLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase',
    color: 'var(--text-muted)',
  },
  livePreviewLayoutName: {
    fontSize: 14, fontWeight: 800, color: 'var(--primary)',
    background: 'rgba(230,81,0,0.1)', padding: '3px 10px', borderRadius: 99,
  },

  presetRow: { display: 'flex', flexWrap: 'wrap', gap: 10, margin: '10px 0 0' },
  presetCard: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', textAlign: 'left',
    background: '#fff', border: '2px solid var(--border)', borderRadius: 10, cursor: 'pointer',
  },
  presetCardActive: { borderColor: 'var(--primary)', boxShadow: '0 2px 10px rgba(230,81,0,0.14)' },
  presetLabel: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' },

  customSection: {
    marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)',
    display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 460,
  },
  customSectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  customGroup: { display: 'flex', flexDirection: 'column', gap: 10 },
  customGroupLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
    color: 'var(--text-muted)',
  },
  colorFieldRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  colorFieldLabelCol: { display: 'flex', flexDirection: 'column', gap: 2 },
  colorFieldLabel: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' },
  colorFieldResetLink: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontSize: 11, fontWeight: 600, color: 'var(--primary)', textAlign: 'left', textDecoration: 'underline',
  },
  colorFieldInputWrap: {
    display: 'flex', alignItems: 'center', gap: 8, border: '1.5px solid var(--border)',
    borderRadius: 8, padding: 4, background: 'var(--bg-page)', flexShrink: 0,
  },
  colorFieldSwatch: {
    width: 30, height: 30, padding: 0, border: 'none', borderRadius: 6, cursor: 'pointer', background: 'none',
  },
  colorFieldText: {
    width: 90, border: 'none', background: 'transparent', outline: 'none',
    fontSize: 13, fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-primary)',
    textTransform: 'uppercase',
  },

  footer: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 28px',
    borderTop: '2px solid var(--border)', background: '#fff', flexShrink: 0,
  },
  footerHint: { fontSize: 12, color: 'var(--text-muted)', flex: 1 },
  nextBtn: {
    padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8,
    boxShadow: '0 2px 10px rgba(230,81,0,0.25)',
  },
  backBtn: {
    padding: '9px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 8,
  },
};
